import { t } from '@trimble/i18n';
import { Component } from 'solid-js';
import { ColorSwatch } from '../../components/ColorSwatch';

interface NewColorStopInputProps {
  /** Current text in the value input (controlled). */
  readonly value: string;
  /** Length unit suffix shown after the value (e.g. 'm', 'ft'). Defaults to 'm'. */
  readonly lengthUnit?: string;
  /** Swatch colour (grey placeholder until the user picks one). */
  readonly color?: string;
  /** Anchor for positioning a colour-picker popover beside the swatch. */
  swatchContainerRef?(el: HTMLDivElement): void;
  onValueInput(value: string): void;
  onSubmit(value: string): void;
  onSwatchClick?(): void;
}

// Neutral level-2 surface colour used while no stop colour has been chosen yet.
export const PLACEHOLDER_SWATCH_COLOR = '#e9eaf0';

/**
 * Presentational input for adding a new colour stop: a swatch, a value field
 * (with unit suffix) and helper text. This is a dumb component — all state and
 * behaviour are driven by props; it holds no store or add logic of its own.
 */
export const NewColorStopInput: Component<NewColorStopInputProps> = (props) => {
  function handleKeyDown(e: KeyboardEvent): void {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    props.onSubmit(props.value);
  }

  return (
    <div
      class="flex flex-col gap-1.5 pb-4 border-b border-gray-0"
      data-testid="new-color-stop-input"
    >
      <span class="text-sm text-gray-6">
        {t('measurements.volumetric.gradient.newStopHelp')}
      </span>
      <div class="flex items-center gap-1.5">
        <div
          class="relative shrink-0"
          ref={(el) => props.swatchContainerRef?.(el)}
        >
          <ColorSwatch
            color={props.color ?? PLACEHOLDER_SWATCH_COLOR}
            testId="new-color-stop-swatch"
            onClick={
              props.onSwatchClick ? () => props.onSwatchClick?.() : undefined
            }
          />
        </div>
        <input
          class="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm min-w-0"
          type="text"
          value={props.value}
          placeholder={t('measurements.volumetric.gradient.newStopPlaceholder')}
          onInput={(e) => props.onValueInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          data-testid="new-color-stop-value"
        />
        <span class="text-sm text-gray-6 shrink-0">
          {props.lengthUnit ?? 'm'}
        </span>
      </div>
    </div>
  );
};
